/*
 * The cover-design picker, shared by the two Alpine components that need it.
 *
 * WHY THIS IS ITS OWN FILE. The step belongs between the occasion and the
 * photos — the design is the card the photos go INTO — and there are two
 * places a customer meets that sequence: the wizard at /pages/get-started,
 * which is where the site's links point, and the builder's own entry, for
 * anyone who lands on /products/picturepop directly. Putting the picker in
 * only one of them (2026-08-05, first attempt) meant everyone coming through
 * the front door chose a design AFTER their photos, or not at all.
 *
 * The two hosts are different Alpine components with different state shapes,
 * so this exports methods that ask the host three questions:
 *
 *   pickerOccasion()   the chosen occasion slug
 *   pickerCard()       the object to write .design onto
 *   onDesignChosen()   what happens next (the wizard advances a step;
 *                      the builder just stops showing the picker)
 *
 * Requires window.PP_DESIGNS_URL and window.PP_ASSET_BASE on the page, and
 * window.PP.OCCASIONS for the band heading.
 */
(function () {
  'use strict';

  window.PP = window.PP || {};

  // A thin occasion page is topped up (designsTopUp): under this many cards, up to TOP_UP_MAX more that
  // suit almost anything. The hard occasions take only gentle ones (the occasion guide, 2026-10-07).
  const TOP_UP_BELOW = 6, TOP_UP_MAX = 12;
  const HARD_OCCASIONS = ['sympathy', 'pet-sympathy', 'get-well'];

  // ⭐ THE ART KEY — the ONE field the printer looks artwork up by (2026-09-10).
  //
  // ⛔ IT IS NOT `id`, AND THAT DISTINCTION IS LOAD-BEARING. `id` means two
  // different things depending on where the catalogue came from:
  //
  //   designs.json   `id` IS the art slug — "grumpy-cat". Always has been.
  //   theme editor   `id` is Shopify's BLOCK id — an opaque token like
  //                  "template--1234__cover-designs-1". Nothing to do with art.
  //
  // So sending `id` as the art key would work perfectly against the shipped
  // catalogue and then, the first time somebody added a design in the theme
  // editor, send garbage — and a garbage key does not degrade, it makes
  // artSlugsFor() refuse the ENTIRE batch, every customer in it, days later.
  //
  // Hence an explicit `artKey`, defaulted per source:
  //   - the theme editor supplies it from the block's own "Art key" field,
  //     and leaves it EMPTY when unset. Empty means print falls back to
  //     slugifying the name, which is exactly the old behaviour — safe.
  //   - designs.json entries fall back to `id`, which is already the slug.
  //
  // See picturepop-theme/sections/cover-designs.liquid, configurator.js
  // (`_Design Id`), and projects/pop-cards/docs/2026-09-08-order-property-contract.md.
  function withArtKey(d) {
    if (!d || typeof d !== 'object') return d;
    if (typeof d.artKey === 'string' && d.artKey) return d;
    // Only designs.json reaches here with a slug-shaped id worth trusting.
    // A CMS entry carries `artKey: ""` from the Liquid and keeps it.
    return Object.assign({}, d, {
      artKey: (typeof d.artKey === 'string') ? d.artKey : (d.id || ''),
    });
  }

  /* ⭐ HOW WELL A DESIGN FITS AN OCCASION: 3 made for it, 2 suits it, 1 a generic card (its cover suits
     any occasion), 0 not on it. The rule itself is the artist platform's (artists-worker
     src/facts/placement.js, from each card's cover since 2026-10-08); the catalogue carries its answer
     as `made`, `suits` and `occasions` (every page, made for first), and this only reads it, as
     cards-catalogue.js does. designs.json entries without `made` were chosen by hand: all made for. */
  function designFit(d, occ) {
    if (!d || !occ || !d.occasions || d.occasions.indexOf(occ) === -1) return 0;
    if (!d.made || d.made.indexOf(occ) !== -1) return 3;
    return d.suits && d.suits.indexOf(occ) !== -1 ? 2 : 1;
  }
  window.PP.designFit = designFit;

  /* A person's day, and who it is for (rd-finding.js PERSON_OF and NOT_WHO, the occasion pages' copies). */
  const PERSON_OF = { 'mothers-day': ['mom'], 'fathers-day': ['dad'], 'grandparents-day': ['grandma', 'grandpa', 'grandparent'],
    valentines: ['partner', 'wife', 'husband'] };
  const NOT_WHO = ['anyone', 'art-lover', 'book-lover', 'pet-lover', 'traveler'];

  /* ⭐ THE OCCASION A DESIGN IS FOR, when nobody named one (Daniel, 2026-10-08: "There should only ever be an
     occasion set for a card if the cover or the cover-inside combination specifically have something to do with a
     specific occasion"): the first it was made for, else the first its cover suits, else Just because for a generic
     card, else none. designs.json entries without `made` were chosen by hand: made for their occasions (designFit).
     ⛔ NEVER `topGreets`. The occasion a stripped inside greeting was once written for decides only whether that
     greeting shows (configurator.js topOnDesign, designTopPlain below), never the card's occasion. Used by the finder
     (finder.js finderOccasionFor); rd-design.js occasionFor and cards-catalogue.js occasionFor keep the same order. */
  function designOccasion(d) {
    if (!d) return '';
    const made = Array.isArray(d.made) ? d.made : (d.occasions || []);
    return made[0] || (d.suits || [])[0] || (d.any === true ? 'just-because' : '');
  }
  window.PP.designOccasion = designOccasion;

  /* Whether a picture of this design's inside top shows its TEXT-FREE top: a design whose inside greeting was
     stripped (catalogue CoverTopPlain) shows it as designed only on an occasion that greeting was written for
     (topGreets); on any other, and with no occasion, the greeting is not what the customer gets. */
  function designTopPlain(d, occ) {
    // topNames: a greeting that names a person ("Happy Birthday, Mom") is never what a fresh card starts on (2026-10-25).
    return !!(d && d.art && d.art.CoverTopPlain && !(occ && !d.topNames && (d.topGreets || []).indexOf(occ) !== -1));
  }
  window.PP.designTopPlain = designTopPlain;

  // Delegates to the one track() in configurator.js, so this file cannot drift
  // into reporting a different shape. Guarded rather than assumed: nothing
  // here depends on load order, and by the time anything is tracked — all of
  // it user-initiated — both files are loaded.
  function track(name, params) {
    try {
      if (window.PP && window.PP.track) window.PP.track(name, params);
    } catch (e) { /* analytics never blocks the picker */ }
  }

  // Ephemeral picker state. `designArtReady` is not ephemeral to the builder —
  // its opening waits on it — so the builder keeps its own copy in state and
  // this one is only the initial value.
  window.PP.designPickerState = function () {
    return {
      designs: [],        // the catalogue, from designs.json
      topShots: {},       // text-free inside tops drawn as the catalogue's top shot, by CoverTopPlain URL (top-shot.js)
      designPeek: null,   // which tile is showing its open shot on a touch screen
      designArtReady: false,
      catalogueComplete: false,   // the artist catalogue answered (see _appendArtistDesigns)
      designWho: null,    // design id -> the people it is for, from catalogue-facts.json, on a person's day only (designsMatched)
    };
  };

  window.PP.designPickerMethods = {
    // A returning visitor already has a design; its art still has to be
    // decoded before anything that waits on designArtReady can run.
    _rehydrateDesign() {
      const card = this.pickerCard();
      if (card && card.design && card.design.id) this.chooseDesign(card.design.id, true);
    },

    // The catalogue as the theme editor has it, or null. Written by the
    // "Cover designs" section (sections/cover-designs.liquid), which builds
    // its JSON by hand in Liquid — so this is parsed defensively. A shop that
    // has typed something this cannot read falls through to the placeholders
    // rather than losing the picker.
    _designsFromCms() {
      const el = document.getElementById('pp-cover-designs');
      if (!el) return null;
      try {
        const parsed = JSON.parse(el.textContent);
        return (parsed && parsed.length) ? parsed.map(withArtKey) : null;
      } catch (e) {
        if (window.console) console.warn('cover-designs section did not parse', e);
        return null;
      }
    },

    // The catalogue, from whichever source has one.
    //
    // The theme editor wins outright when it has anything in it — adding one
    // design there REPLACES the shipped placeholders rather than joining them,
    // because two catalogues merged would mean neither could be emptied.
    //
    // assets/designs.json is the fallback: the three placeholder designs the
    // theme ships with, so the flow works before anything is configured.
    // Failure of both is survivable — the picker says it has nothing.
    loadDesigns() {
      const self = this;
      const fromCms = this._designsFromCms();
      if (fromCms) {
        this.designs = fromCms;
        this._rehydrateDesign();
        return this._appendArtistDesigns();
      }
      if (!window.PP_DESIGNS_URL) return this._appendArtistDesigns();
      return fetch(window.PP_DESIGNS_URL)
        .then(function (r) { return r.json(); })
        .then(function (j) {
          self.designs = (((j && j.designs) || [])).map(withArtKey);
          self._rehydrateDesign();
        })
        .catch(function (e) {
          if (window.console) console.warn('design catalogue failed to load', e);
        })
        .then(function () { return self._appendArtistDesigns(); });
    },

    /* ⭐ ARTISTS' PUBLISHED DESIGNS, from artists.picturepops.com/catalogue.json, AFTER the
       store's own catalogue (Daniel, 2026-09-17: publishing an approved design must not need
       a theme push). Same shape as designs.json, absolute URLs (designUrl passes them through),
       art keys all `artist-…`. NEVER in the way: capped at 3 seconds, and any failure leaves
       the store's own designs exactly as they were. */
    _appendArtistDesigns() {
      const self = this;
      const url = window.PP_ARTIST_CATALOGUE_URL || 'https://artists.picturepops.com/catalogue.json';
      const timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 3000); });
      /* The fetch itself, kept past the 3-second race (18 Oct): a link that names an artist design (?design=) waits
         for it rather than sending the visitor away when the catalogue is slow (configurator.js, artistLate). On a
         slow connection (Daniel's Safari, 18 Oct) every card from the reviewers' page bounced back to it. */
      const full = fetch(url, { credentials: 'omit' }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
      self._artistFull = full.then(function (j) {
        if (!j || !j.designs || self.catalogueComplete) return;
        self.catalogueComplete = true;
        const extra = j.designs.filter(function (d) {
          return d && d.id && !(self.designs || []).some(function (x) { return x.id === d.id; });
        }).map(withArtKey);
        if (extra.length) { self.designs = (self.designs || []).concat(extra); self._rehydrateDesign(); }
      });
      return Promise.race([full, timeout])
        .then(function (j) {
          /* Whether the WHOLE catalogue is here: a design missing from a complete
             catalogue was withdrawn; missing from a timed-out one, it may just not
             have arrived (configurator.js reconcileDesignToOccasion). */
          self.catalogueComplete = !!(j && j.designs);
          const extra = ((j && j.designs) || []).filter(function (d) {
            return d && d.id && !(self.designs || []).some(function (x) { return x.id === d.id; });
          }).map(withArtKey);
          if (!extra.length) return;
          self.designs = (self.designs || []).concat(extra);
          self._rehydrateDesign();
        })
        .catch(function () { /* the store's own catalogue stands */ });
    },

    // Every design on this occasion, in one grid: the ones made FOR it first, then the ones whose cover
    // suits it, then the generic ones (Daniel, 2026-10-08: "if I'm in an occasion page for birthday, I
    // have a bunch of cards [made for it]. And then I also have other cards that fit"). designFit()
    // above reads the catalogue's answer; within a tier the catalogue's own order stands.
    /* ⭐ AND ON A PERSON'S DAY, the generic cards for that person come before the other generic cards (theme
       e9eb4d4, Daniel 9 Oct; rd-finding.js sortList `order`, the occasion pages' twin): Mother's Day opens with the
       cards made for it, then those that suit it, then a card for Mom, then the patterns. Who a card is for is in
       catalogue-facts.json, which only this one case needs, so it is fetched then and nowhere else; until it
       answers the generic cards keep the catalogue's order, as before. */
    designsMatched() {
      const occ = this.pickerOccasion();
      const all = this.designs.filter(function (d) { return designFit(d, occ) > 0; });
      const people = PERSON_OF[occ];
      if (people && !this.designWho) this._loadDesignWho();
      const who = this.designWho || {};
      const forDay = function (d) { return !!people && (who[d.id] || []).some(function (p) { return people.indexOf(p) !== -1; }); };
      const tier = function (d) { const f = designFit(d, occ); return f === 1 && forDay(d) ? 1.5 : f; };
      return [3, 2, 1.5, 1].reduce(function (out, t) {
        return out.concat(all.filter(function (d) { return tier(d) === t; }));
      }, []);
    },
    _loadDesignWho() {
      if (this._designWhoAsked) return;
      this._designWhoAsked = true;
      const self = this;
      const url = (window.PP_ARTIST_CATALOGUE_URL || 'https://artists.picturepops.com/catalogue.json').replace(/catalogue\.json$/, 'catalogue-facts.json');
      fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
        const out = {};
        ((j && j.designs) || []).forEach(function (d) {
          const f = d.filters || (d.facts && d.facts.filters);
          out[d.id] = f && Array.isArray(f.who) ? f.who
            : ((d.facts && d.facts.recipients) || []).filter(function (w) { return NOT_WHO.indexOf(w) === -1; });
        });
        self.designWho = out;
      }).catch(function () { /* the catalogue's order stands */ });
    },
    designFitFor(d, occ) { return designFit(d, occ); },

    /* A THIN PAGE, TOPPED UP (the site map, Part 2: no occasion is left empty). Under TOP_UP_BELOW
       cards, the shelf goes on with cards that suit almost anything: the ones placed on Just because.
       A hard occasion (sympathy, pet sympathy, get well) takes only cards that are gentle (the
       catalogue's `gentle`: no light tone at all, facts/placement.js) AND placed on Thinking of you,
       the guide's occasion for hard times too. "Not a joke" was not enough: on 7 Oct the gentle
       Just-because cards included love puns and Zodiac birthday cards. The line says what they are. */
    designsTopUp() {
      const occ = this.pickerOccasion();
      if (!occ || occ === 'my-designs' || occ === 'just-because') return [];
      const matched = this.designsMatched();
      if (matched.length >= TOP_UP_BELOW) return [];
      const hard = HARD_OCCASIONS.indexOf(occ) !== -1;
      const have = matched.map(function (d) { return d.id; });
      return this.designs.filter(function (d) {
        if (have.indexOf(d.id) !== -1 || !d.occasions) return false;
        return hard ? d.gentle === true && d.occasions.indexOf('thinking-of-you') !== -1
                    : d.occasions.indexOf('just-because') !== -1;
      }).slice(0, TOP_UP_MAX);
    },

    // The line over a topped-up shelf, in the words of the site map's occasion pages.
    topUpLine() {
      const occ = this.pickerOccasion();
      const hard = HARD_OCCASIONS.indexOf(occ) !== -1;
      const why = hard ? 'These are gentle enough for it, and every card carries your own words.'
                       : 'Every card carries your own words, and these suit almost anything.';
      const n = this.designsMatched().length, label = this.occasionLabelOf(occ);
      if (!n) return 'No ' + label + ' designs yet. ' + why;
      return (n === 1 ? 'The first one is for ' + label + '; after it, ' : 'The first ' + n + ' are for ' + label + '; after them, ') +
             (hard ? 'gentle cards that also work. ' : 'cards that suit almost anything. ') +
             'Every card carries your own words.';
    },

    designsUniversal() {
      // Artist mode's "My designs" shows the artist's designs and nothing else.
      if (this.pickerOccasion() === 'my-designs') return [];
      return this.designs.filter(function (d) {
        return !d.occasions || !d.occasions.length;
      });
    },

    designChosen() {
      const card = this.pickerCard();
      return !!(card && card.design && card.design.id);
    },

    // Resolve a design's image. The theme-editor catalogue already holds full
    // CDN URLs; designs.json holds bare filenames, because it is hand-edited
    // data and cannot run Liquid filters to build CDN paths for itself.
    /* A grid picture at the size a tile draws it (2026-10-11, N1). Shopify's CDN resizes and picks the format
       for the browser (WebP/AVIF) when asked for a width; the full 2400 px master was being sent to a ~300 px
       tile. Anything else -- the artists' catalogue (it serves one size), a blob: top shot -- passes unchanged. */
    designThumbUrl(url, w) {
      if (!url || url.indexOf('/cdn/shop/') === -1 || /[?&]width=/.test(url)) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (w || 800);
    },
    /* Which grid tiles load at once: the first rows, the ones on screen when the grid opens. The rest are lazy
       (design-tile.liquid). Eight covers two rows of the desktop grid and the first screens of the phone list. */
    tileEager(i) { return i >= 0 && i < 8; },

    designUrl(file) {
      if (!file) return '';
      // A full URL passes: http(s), protocol-relative, and the blob: of a drawn top shot (designTopShot).
      if (file.indexOf('//') === 0 || file.indexOf('http') === 0 || file.indexOf('blob:') === 0) return file;
      return (window.PP_ASSET_BASE || '') + file;
    },

    // The tile's hover image: the card OPEN, because the inside top is as much
    // a part of the design as the front and a closed card never shows it.
    // Falls back to the CoverTop art — that same face, undressed.
    /* Everything on offer for this occasion, in the order the screen shows it.
       Two bands exist because designs made FOR an occasion should lead; when
       there is only one card in total the bands are a distinction without a
       difference, and the screen says something else entirely. */
    /* ⛔ NO UNIVERSAL BAND. This used to be
         designsMatched().concat(designsUniversal())
       and that concat is why Flower appeared under every occasion — a design
       tagged `occasions: []` was appended to all of them (Daniel, 2026-09-10:
       "you added the flower design to every single occasion, which is wrong").
       Flower now belongs to `just-because` like every other design belongs
       somewhere, so nothing is universal and the band had nothing in it.
       ⚠️ IT IS REMOVED RATHER THAN LEFT EMPTY on purpose. A mechanism that
       silently puts a design on every occasion, with nothing currently in it,
       is a trap for whoever adds the next `occasions: []` design and cannot
       work out why it is everywhere. designsUniversal() is kept — see below —
       so bringing the band back is one concat. */
    designsOffered() {
      // A search's answer, in its own order (assets/finder.js).
      if (this.finder && typeof this.finderShown === 'function') return this.finderShown();
      return this.designsMatched().concat(this.designsTopUp());
    },

    /* One card is not a choice. Asking someone to "pick a design" from a set of
       one reads as a broken screen, so the solo case drops the question and
       shows the card as the thing it is, with the rest of the shelf still to
       come. Daniel, 2026-09-02. */
    isSoloDesign() { return this.designsOffered().length === 1; },

    designHasOpenShot(d) { return !!(d && d.preview && d.preview.open); },

    /* The tile face. `preview.closed` is a real product shot -- the card
       photographed almost shut, already shaped, shadowed and cut out -- so the
       tile shows it whole. Without one the tile falls back to the flat
       CoverFront artwork and CSS fakes the paper: a rounded, shadowed rectangle
       at the cover's own proportion. Both look like a card; only one IS one. */
    designHasClosedShot(d) { return !!(d && d.preview && d.preview.closed); },

    designClosedUrl(d) {
      if (this.designHasClosedShot(d)) return this.designUrl(d.preview.closed);
      return this.designUrl(d.art.CoverFront);
    },

    designOpenUrl(d) {
      if (this.designHasOpenShot(d)) return this.designUrl(d.preview.open);
      return this.designUrl(d.art.CoverTop);
    },

    /* The tile's SECOND picture, and the sheet's "Inside". `preview.top` is
       the inside-top face shaped like a card -- corners rounded at its cut
       edge, a shadow at its foot -- rendered straight from the print master.
       It took the open shot's place on the grid on 2026-09-02: at tile size
       the open card was "not really legible and not giving enough value"
       (Daniel), and the face itself is what a customer is choosing. Without
       one the tile falls back to the flat CoverTop artwork. */
    designHasTopShot(d) { return !!(d && d.preview && d.preview.top); },

    /* ⛔ NOT THE GREETING ON AN OCCASION IT WAS NOT WRITTEN FOR (2026-10-08). `preview.top` is rendered with the
       design's inside greeting, so a stripped design ("You quack me up", greeting for birthday) showed "Happy
       birthday!" on the Friendship page. Off its greeting's occasions, the picture is the text-free top, drawn as the
       same top shot by assets/top-shot.js (designTopShot) once it has been made, the flat CoverTopPlain until then. */
    designTopUrl(d) {
      if (this.designTopIsPlain(d)) return this.topShots[d.art.CoverTopPlain] || this.designUrl(d.art.CoverTopPlain);
      if (this.designHasTopShot(d)) return this.designUrl(d.preview.top);
      return this.designUrl(d.art.CoverTop);
    },
    // The occasion a picture of this design is for: the finder's pick for it in a search, else this page's.
    designOccasionShown(d) {
      if (this.finder && typeof this.finderOccasionFor === 'function') return this.finderOccasionFor(d);
      return this.pickerOccasion();
    },
    designTopIsPlain(d) { return designTopPlain(d, this.designOccasionShown(d)); },
    /* Draws the text-free top as a top shot, once per design, and swaps it in (designTopUrl, configurator.js
       sheetShots). Asked for when a design's sheet opens, never for a whole grid: each is a canvas and an encode. */
    designTopShot(d) {
      const url = d && d.art && d.art.CoverTopPlain;
      if (!url || this.topShots[url] || !(window.PP && window.PP.topShot)) return;
      const self = this;
      window.PP.topShot(this.designUrl(url)).then(function (shot) {
        if (shot) self.topShots = Object.assign({}, self.topShots, { [url]: shot });
      });
    },

    // Touch screens have no hover. `@click.stop` on the button keeps this from
    // also choosing the design underneath it.
    togglePeek(id) { this.designPeek = (this.designPeek === id) ? null : id; },

    // Writes card.design, which is all the builder's stage needs: the cover
    // planes bind straight to designArt(), and `card` is already persisted —
    // by the wizard into pp_config_v2 at handoff, by the builder directly.
    //
    // `rehydrating` is the returning-visitor path: same decode, but it must
    // not advance a step or fire an event the customer didn't cause.
    chooseDesign(id, rehydrating) {
      const d = this.designs.filter(function (x) { return x.id === id; })[0];
      if (!d) return;
      // Chosen from a search, before any occasion was asked: the occasion
      // follows from the design (finder.js).
      if (!rehydrating && this.finder && typeof this.finderBeforeChoose === 'function') this.finderBeforeChoose(d);
      this.artistSheet = null;     // "Add your pictures" from an artist's profile
      const self = this;
      const card = this.pickerCard();
      if (!card) return;

      card.design = {
        id: d.id,
        name: d.name,
        /* ⛔ THE PRINT ART KEY. buildCartProperties() sends it as `_Design Id`, and it was never
           copied here — so from 2026-09-10 to 2026-09-17 every order sent `_Design Id: ""` and
           print fell back to slugifying the NAME. Harmless for the store's own designs (their
           names slugify to their ids); fatal for an artist design, whose art lives under
           `artist-…` and whose name slugifies to nothing print has. Found 2026-09-17 while
           publishing artist designs; seen empty on orders #1022 and #1023. */
        artKey: d.artKey || '',
        /* WHERE IT MAY BE SENT (2026-09-25): a design free to use in some countries and not
           others - Winnie-the-Pooh is public domain in the US, protected in the UK and Australia -
           carries the countries it may go to (artists.picturepops.com catalogue.json shipsTo).
           The recipients step refuses any other (configurator.js recipBad); print holds any that
           slips through. Absent: anywhere. */
        shipsTo: Array.isArray(d.shipsTo) && d.shipsTo.length ? d.shipsTo.slice() : null,
        CoverFront: this.designUrl(d.art.CoverFront),
        CoverTop: this.designUrl(d.art.CoverTop),
        // The outside of the bottom page (v2e's eighth plane, CoverBack): seen when the card turns over or spins.
        CoverBack: d.art.Back ? this.designUrl(d.art.Back) : '',
        // The inside lower panel is TWO optional layers, and they answer two
        // different questions. Empty string, not undefined — designArt()
        // returns these straight to an x-show and a missing key reads as a
        // bound error.
        //
        //   CoverBottomGround  what COLOUR the panel is. Always drawn, under
        //                      everything, and it never suppresses the note —
        //                      the customer writes on top of it.
        //   CoverBottom        what the design SAYS there, if anything. Drawn
        //                      instead of the note, and only while the customer
        //                      has kept it (showDesignMessage()).
        //
        // Print makes the same split as bottom.png / bottom-message.png; see
        // print/art/README.md. Keeping the two questions apart is what stops
        // "this design has a background" from being read as "this design
        // already says something", which on the print side silently binned
        // customer notes until 2026-09-04.
        CoverBottomGround: d.art.CoverBottomGround ? this.designUrl(d.art.CoverBottomGround) : '',
        CoverBottom: d.art.CoverBottom ? this.designUrl(d.art.CoverBottom) : '',
        /* THE PHOTO STRIP BORDER (Part B, 2026-09-19): the paper around the four
           photos, 218 x 78.9 mm with its 2 mm bleed, drawn by the design's artist.
           Optional — most designs have none and the strip stays white. Print has
           it as strip.png beside the faces; this is the web copy. See
           panelStrip() in configurator.js, and the paragraph just below: this is
           NOT the reverted design-wide ground. */
        Strip: d.art.Strip ? this.designUrl(d.art.Strip) : '',
        /* THE INSIDE TOP AS A CHOICE (2026-10-08): on a generic cover whose inside greeting was
           stripped, the text-free top, the occasions that greeting was for, and where the words may
           go (artists catalogue.json CoverTopPlain / topGreets / topBox). configurator.js
           hasTopChoice() reads them; empty means the design keeps its own inside top, as before. */
        CoverTopPlain: d.art.CoverTopPlain ? this.designUrl(d.art.CoverTopPlain) : '',
        topGreets: Array.isArray(d.topGreets) ? d.topGreets.slice() : [],
        topNames: d.topNames === true,   // the greeting names a person: start on the occasion's words (2026-10-25)
        topBox: Array.isArray(d.topBox) && d.topBox.length === 2 ? d.topBox.slice() : null,
        /* What step 3 of the builder's direction B offers (2026-10-08, decisions.md "Every card's inside top"):
           'words', 'photo' or 'keep', from the artists catalogue (catalogue.js topChoiceOf); designs.json may set
           it for a theme design. Empty means not read yet: builder-b.js treats that as keep unless the design has
           a text-free top. Today's builder does not read it. */
        topChoice: ['words', 'photo', 'keep'].indexOf(d.topChoice) !== -1 ? d.topChoice : '',
      };
      this.designArtReady = false;

      /* ⛔ THE INSIDE DOES NOT TAKE THE DESIGN'S GROUND — STILL TRUE, AND
         CoverBottomGround IS NOT A WAY BACK TO IT. Read this before widening
         anything.

         A design-wide `bg` was tried and reverted the same day (Daniel,
         2026-09-01). The reasoning for it was sound — cover and accordion are
         printed on one sheet, so a white accordion behind a pink cover reads as
         two cards glued together. On screen it was the opposite problem: with
         the accordion panels, the paper backs AND the note panel all taking the
         design's pink, the open card was overwhelmingly pink and the customer's
         photographs had nothing to sit against. "There's too much pink here."

         So the accordion panels and the paper backs still stay
         --pp-card-paper's own white, and there is still no design-wide colour.
         What CoverBottomGround adds is ONE face — the note panel — and only
         because print already prints a ground there and the builder was not
         showing it (Daniel, 2026-09-04: "if the bottom is anything at all it
         needs to show up in the builder"). One panel is not the flood that got
         reverted; the photographs still sit on white. Do not extend it to the
         other planes without re-reading the paragraph above. Git has the old
         design-wide `bg` at 3883c40.

         ⭐ THE PART B BORDER (2026-09-19) IS THE EXCEPTION, AND IT IS A DIFFERENT
         THING. It is not a colour flooding every plane: it is a border an artist
         drew around the photographs on purpose, only on the designs that have
         one, and it PRINTS on the strip. The rule that decides it is Daniel's own
         from 2026-09-04 — what prints has to show in the builder. So the four
         photo panels take a design's Strip when it has one, and nothing else
         changes: the paper backs, the note panel and every design without a
         Strip stay exactly as above. */

      // Decode BOTH faces before saying ready. CoverFront and CoverTop are the
      // two sides of one panel and the opening shows one then the other, so a
      // half-loaded design pops mid-animation.
      const urls = [card.design.CoverFront, card.design.CoverTop];
      let left = urls.length;
      urls.forEach(function (u) {
        const img = new Image();
        const done = function () { if (--left === 0) self.designArtReady = true; };
        img.onload = done;
        img.onerror = done;          // never let a missing file block the card
        img.src = u;
      });

      // The inside top follows the design and the occasion until the customer makes it theirs (configurator.js).
      if (typeof this.topOnDesign === 'function') this.topOnDesign();

      if (rehydrating) return;
      track('pp_design_chosen', this.finder
        ? { design: d.id, from: 'finder', position: this.finderPosition(d.id) } : { design: d.id, from: 'occasion' });
      this.designPeek = null;
      if (typeof this.onDesignChosen === 'function') this.onDesignChosen();
    },

    /* ───── The occasion page ────────────────────────────────────────
     * The catalogue screen at ?occasion=<slug>: a title, a sub, a shelf of
     * the designs that serve this occasion, and — while there is room on the
     * shelf — a veil that says more are coming and offers to write.
     */

    /* h1 / h2. From the OCCASION record, never hardcoded here, so copy is a
       one-line edit in configurator.js next to the phrase and headline it has
       to agree with. Falls back to the label so an occasion added without
       copy still renders a page rather than an empty heading. */
    occasionTitle() {
      if (this.finder && typeof this.finderTitle === 'function') return this.finderTitle();
      const p = (window.PP.OCCASION_PAGE || {})[this.occasion] || {};
      return p.title || (this.occasionLabelOf(this.occasion) + ' cards');
    },
    occasionSubtitle() {
      if (this.finder && typeof this.finderSubtitle === 'function') return this.finderSubtitle();
      const p = (window.PP.OCCASION_PAGE || {})[this.occasion] || {};
      return p.subtitle || '';
    },

    /* THE SHELF IS ALWAYS THREE WIDE, and the veil takes whatever the designs
       do not. n=1 leaves a veil spanning two slots, n=2 leaves one, n=3 fills
       the row and the veil drops to its own row beneath. Past three the cards
       wrap and the ghost row stays only while we are still expecting more.

       Returning a NUMBER of empty slots rather than a state name keeps the
       three "states" in the handoff as what they are — one layout counting to
       three — so shipping a design moves nothing but the count. */
    /* The live column count, read off the grid itself so the veil spans what
       is actually left on the last row. Fixed at 3 until 2026-09-06, when the
       shelf became auto-fill (four columns on a wide screen, three on a
       tablet); a veil that still counted to three left a hole at four. */
    cols: 3,
    shelfSlots() { return this.cols || 3; },
    _watchCols() {
      const self = this;
      const shelf = document.querySelector('.pp-design .pp-shelf');
      if (!shelf) return;
      const read = function () {
        if (self.narrow) return;
        const cs = getComputedStyle(shelf);
        // Hidden while the catalogue loads: no tracks to count yet. Reading
        // "none" here would set cols to 1 and span the veil wrong until the
        // next signal — leave it alone and let a later read do it.
        if (cs.display === 'none') return;
        const tracks = cs.gridTemplateColumns.split(' ').filter(Boolean).length;
        if (tracks && tracks !== self.cols) self.cols = tracks;
      };
      /* Every signal that can change the count, not just one. ResizeObserver
         is delivered on the frame loop, so a hidden or throttled tab may never
         run it; the shelf also goes display:none -> grid when the catalogue
         lands, and the desktop/narrow boundary swaps the layout outright. */
      if ('ResizeObserver' in window) new ResizeObserver(read).observe(shelf);
      window.addEventListener('resize', read, { passive: true });
      this.$watch('designs', function () { self.$nextTick(read); });
      this.$watch('occasion', function () { self.$nextTick(read); });
      this.$watch('narrow', function () { self.$nextTick(read); });
      read();
      setTimeout(read, 1500);   // last resort, after everything has settled
    },
    veilSpan() {
      if (this.finder) return 0;          // a search's answer is not an occasion's shelf
      const n = this.designsOffered().length;
      if (!n) return 0;
      return Math.max(0, this.shelfSlots() - (n % this.shelfSlots() || this.shelfSlots()));
    },
    /* The ghost ROW — a full-width veil under a complete row of cards. Only
       when the row is full and we still expect more. */
    showGhostRow() {
      if (this.finder) return false;
      const n = this.designsOffered().length;
      return n > 0 && this.veilSpan() === 0 && this.expectingMore();
    },
    /* Until an occasion record carries the flag, every occasion is expecting
       more: the catalogue is four designs old. Flip this to a per-occasion
       field the day one is considered finished. */
    expectingMore() { return true; },

    /* The veil's background is the occasion's own card art, blurred past
       legibility — a shelf that is empty because the shop is new, not broken.
       One transform per slot so two ghosts never look like the same image
       pasted twice. */
    veilArt() {
      const list = this.designsOffered();
      const d = list[list.length - 1];
      return d ? this.designClosedUrl(d) : '';
    },

    /* ───── Mobile: narrow flag, list/grid view, motion ──────────────────
     * Handoff "Occasion page — mobile (390px)", 2026-09-06.
     *
     * Under 900px the shelf is a LIST (one 290px column) or a GRID (2-up),
     * with a segmented toggle in a toolbar. Desktop is always the 3-up shelf
     * and never sees any of this.
     *
     * The breakpoint is CSS, but the VEIL renders differently per view —
     * a banner with the form inside (list) or a message-only cell plus a
     * band under the grid (grid) — so Alpine has to know the width as well.
     * `narrow` mirrors the media query; nothing else reads window.innerWidth.
     */
    narrow: false,
    view: 'list',
    _viewTouched: false,

    occasionViewInit() {
      const self = this;
      const mq = window.matchMedia('(max-width: 899px)');
      const apply = function () { self.narrow = mq.matches; };
      apply();
      if (mq.addEventListener) mq.addEventListener('change', apply); else mq.addListener(apply);

      /* Per-device memory of the toggle. Only a saved choice counts as
         "touched"; the default below is re-derived until then. */
      let saved = null;
      try { saved = localStorage.getItem('pp-occasion-view'); } catch (e) { /* private mode */ }
      if (saved === 'list' || saved === 'grid') { this.view = saved; this._viewTouched = true; }

      this.$watch('designs', function () { self._defaultView(); });
      this.$watch('occasion', function () { self._defaultView(); });
      this._defaultView();
      this.$nextTick(function () { self._revealSetup(); self._watchCols(); });
    },

    /* Default: list while n <= 2, grid at n >= 3 — until the user has toggled. */
    _defaultView() {
      if (this._viewTouched) return;
      this.view = this.designsOffered().length >= 3 ? 'grid' : 'list';
    },

    viewCount() {
      const n = this.designsOffered().length;
      return n === 1 ? '1 design' : n + ' designs';
    },

    setView(v) {
      if (v === this.view) return;
      this._viewTouched = true;
      try { localStorage.setItem('pp-occasion-view', v); } catch (e) { /* private mode */ }
      this._flipView(v);
    },

    /* FLIP the card images and the veil panel between their list and grid
       rects, 250ms with a 30ms stagger. Name/meta/CTA crossfade instead —
       their shape changes too much to travel. Reduced motion: the class just
       swaps and the 150ms opacity transition in the CSS is all that plays. */
    _flipView(v) {
      const self = this;
      const shelf = document.querySelector('.pp-design .pp-shelf');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const items = shelf ? Array.prototype.slice.call(
        shelf.querySelectorAll('.pp-dtile__stage, .pp-veil--mobile .pp-veil__panel')) : [];
      if (reduce || !shelf || !items.length) { this.view = v; return; }

      const before = items.map(function (el) { return el.getBoundingClientRect(); });
      shelf.classList.add('is-switching');            // text: hide now, fade back after
      this.view = v;

      this.$nextTick(function () {
        items.forEach(function (el, i) {
          const a = before[i], b = el.getBoundingClientRect();
          if (!a.width || !b.width) return;
          const dx = a.left - b.left, dy = a.top - b.top;
          const sx = a.width / b.width, sy = a.height / b.height;
          el.style.transformOrigin = 'top left';
          el.style.transition = 'none';
          el.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(' + sx + ', ' + sy + ')';
          el.getBoundingClientRect();                  // commit the inverted frame
          el.style.transition = 'transform 250ms cubic-bezier(.22,.61,.36,1) ' + (i * 30) + 'ms';
          el.style.transform = '';
          /* Cleanup on transitionend — AND on a timer. A background tab, a
             display:none ancestor or a skipped transition never fires the
             event, and an inverted transform left in place pins the card to
             its OLD rect for good. Seen on Draft in the hidden pane. */
          let cleaned = false;
          const done = function () {
            if (cleaned) return; cleaned = true;
            el.style.transition = ''; el.style.transformOrigin = '';
            el.removeEventListener('transitionend', done);
          };
          el.addEventListener('transitionend', done);
          setTimeout(done, 250 + i * 30 + 150);
        });
        // The text fades back in over 150ms (CSS) once this class comes off.
        // A frame AND a timer: a throttled or hidden tab may never paint a
        // frame, and a shelf left in .is-switching has no names, prices or
        // buttons at all. Seen on Draft.
        const unswitch = function () { shelf.classList.remove('is-switching'); };
        requestAnimationFrame(unswitch);
        setTimeout(unswitch, 40);
      });
    },

    /* Scroll-in reveal, once per session: each card fades in and rises 12px
       as it enters the viewport, 60ms apart. Desktop has no reveal in its
       spec. Reduced motion or a repeat visit this session: everything is
       simply visible. */
    _revealSetup() {
      if (!this.narrow) return;
      const shelf = document.querySelector('.pp-design .pp-shelf');
      if (!shelf || !('IntersectionObserver' in window)) return;
      let seen = false;
      try { seen = sessionStorage.getItem('pp-occasion-revealed') === '1'; } catch (e) {}
      if (seen || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      shelf.classList.add('pp-reveal');
      const isCard = function (k) { return k.matches && k.matches('.pp-dtile, .pp-veil'); };
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          const kids = Array.prototype.filter.call(shelf.children, isCard);
          const i = Math.max(0, kids.indexOf(e.target));
          e.target.style.transitionDelay = (i * 60) + 'ms';
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      }, { threshold: 0.15 });
      const observe = function () {
        Array.prototype.forEach.call(shelf.children, function (k) {
          if (isCard(k) && !k.dataset.ppObs) { k.dataset.ppObs = '1'; io.observe(k); }
        });
      };
      observe();
      new MutationObserver(observe).observe(shelf, { childList: true });
      try { sessionStorage.setItem('pp-occasion-revealed', '1'); } catch (e) {}
      // Safety: whatever the observer did or did not do, nothing stays hidden
      // past this. A reveal that can fail leaves an empty shelf, and an empty
      // shelf is worse than a missed animation.
      setTimeout(function () {
        Array.prototype.forEach.call(shelf.children, function (k) { if (isCard(k)) k.classList.add('is-in'); });
      }, 1500);
    },

    /* ───── Notify me ────────────────────────────────────────────────── */
    notifyEmail: '',
    notifyState: 'idle',      // idle | sending | done | error
    notifyError: '',

    /* ⚠️ THE ENDPOINT THE HANDOFF NAMES DOES NOT EXIST. It asks for "the same
       list as the builder email step" — that step, its customer-form post and
       its pp_email cookie were all removed on 2026-09-03 ("Remove name and
       email for launch to add later as an a/b test", decisions.md).

       So this posts to Shopify's own customer form, which is what
       sections/newsletter-signup.liquid already uses and therefore the one
       list that does exist. Same endpoint, sent by fetch rather than by a
       page navigation, because a reload here would drop the customer out of
       a builder they have not finished. The source tag the handoff asks for
       rides along as contact[tags]. */
    async notifySubmit() {
      const email = String(this.notifyEmail || '').trim();
      if (this.notifyState === 'sending') return;
      // Let the browser say it first; this is the backstop for a paste.
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        this.notifyState = 'error';
        this.notifyError = 'That address does not look right.';
        return;
      }
      this.notifyState = 'sending';
      this.notifyError = '';
      const body = new URLSearchParams({
        form_type: 'customer',
        utf8: '\u2713',
        'contact[email]': email,
        'contact[tags]': 'occasion-notify:' + (this.occasion || 'unknown'),
      });
      try {
        const r = await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
credentials: 'same-origin',
        });
        // Shopify answers a customer-form post with a PAGE, not JSON, and
        // answers a duplicate address with a 200 as well. Anything that is
        // not a network or server failure counts as on the list: telling
        // somebody already subscribed that it failed is the worse error.
        if (!r.ok && r.status !== 302) throw new Error('HTTP ' + r.status);
        this.notifyState = 'done';
        track('pp_occasion_notify', { occasion: this.occasion });
      } catch (e) {
        this.notifyState = 'error';
        this.notifyError = 'That did not send. Try again in a moment.';
      }
    },

    // Human label for a slug, and the tail of "For ___" over the matched band.
    // The phrase is stored in OCCASIONS rather than derived because English
    // decides the article per word — "a birthday", but "Christmas".
    occasionLabelOf(slug) {
      const list = window.PP.OCCASIONS || [];
      const o = list.filter(function (x) { return x.slug === slug; })[0];
      return o ? o.label : String(slug || '');
    },

    occasionPhrase() {
      const list = window.PP.OCCASIONS || [];
      const o = list.filter(function (x) { return x.slug === this.pickerOccasion(); }, this)[0];
      return o ? o.phrase : 'this';
    },
  };
})();
